import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getErrorMessageFromErrorResponse } from "shared/welda/helpers/serverResponseParsers";
import { getMessageFromError } from "../../../shared/welda/services/utilsService";
import errorHandling from "../errorHandling";
import MMSService from "./MMSService";

// Get user from localStorage

export const initialState = {
  products: [],
  products_temp: [],
  recommendedProducts: [],
  isLoadingRecommendedProducts: false,
  sortBy: null,
  isAsc: true,
  searchParams: {
    keywords: "",
    hiddenKeywords: "",
    search_geblokart: false,
    search_replacements: false,
    // search_stockart removed — stock lives in CAC, not product search filters
    rode_prijslijst: false,
    search_barcode: "",
    search_artikelnr: "",
    search_naam: "",
    Category: "",
    primaryCategoryId: "",
    query: {},
    merk: "",
    search_verhuur: false,
    reedsaangekocht: false,
    active: false,
  },
  forceSearch: false,
  categoriesState: {
    isLoading: false,
    isError: false,
    isSuccess: false,
    categories: [],
    dossier: null,
  },
  isError: false,
  isSuccess: false,
  isLoading: false,
  isLoadingTemp: false,
  message: "",
};

export const STALE_SEARCH_REJECTED = "deze request is te laat";

const searchStartedAt = {
  products: null,
  products_temp: null,
};

const searchDestination = (value) =>
  value === "products_temp" ? "products_temp" : "products";

export function sortProductsArray(products, sortBy, isAsc) {
  const kleurOrder = { danger: 0, warning: 1, success: 2 };
  return [...(products ?? [])].sort((a, b) => {
    if (sortBy === "Nummer") {
      return isAsc
        ? (a.Nummer ?? "").localeCompare(b.Nummer ?? "")
        : (b.Nummer ?? "").localeCompare(a.Nummer ?? "");
    }
    if (sortBy === "Prijs") {
      return isAsc
        ? (a.Prijs ?? 0) - (b.Prijs ?? 0)
        : (b.Prijs ?? 0) - (a.Prijs ?? 0);
    }
    if (sortBy === "Voorraad") {
      return isAsc
        ? (kleurOrder[a.voorraadKleur] ?? -1) -
        (kleurOrder[b.voorraadKleur] ?? -1)
        : (kleurOrder[b.voorraadKleur] ?? -1) -
        (kleurOrder[a.voorraadKleur] ?? -1);
    }

    if (sortBy === "Relevance" && a.scoring && b.scoring) {
      return isAsc
        ? (a.scoring.scoreCombined ?? 0) - (b.scoring.scoreCombined ?? 0)
        : (b.scoring.scoreCombined ?? 0) - (a.scoring.scoreCombined ?? 0);
    }

    return 0;
  });
}

export const SearchProducts = createAsyncThunk(
  "mms/SearchProducts",
  async (data, thunkAPI) => {
    const { to = "products", ...SearchparamState } = data;
    const destination = searchDestination(to);
    searchStartedAt[destination] = Date.now();
    const Searchparams = {
      ...SearchparamState,
      keywords: `${SearchparamState.hiddenKeywords
        ? SearchparamState.hiddenKeywords + " "
        : ""
        }${SearchparamState.keywords}`,
    };
    const mytime = searchStartedAt[destination];
    try {
      const result = await MMSService.SearchProducts(Searchparams);
      if (searchStartedAt[destination] === mytime) {
        return {
          ...result,
          to: destination,
        };
      }
      return thunkAPI.rejectWithValue(STALE_SEARCH_REJECTED);
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getRecommendedProducts = createAsyncThunk(
  "mms/getRecommendedProducts",
  async (data, thunkAPI) => {
    try {
      const result = await MMSService.getRecommendedProducts(data);
      const { products } = result;
      const payload = {
        products,
      };
      return payload;
    } catch (error) {
      const message = getErrorMessageFromErrorResponse(error);
      errorHandling(error);
      const payload = { message };
      return thunkAPI.rejectWithValue(payload);
    }
  }
);

export const getCategories = createAsyncThunk(
  "mms/getCategories",
  async (data, thunkAPI) => {
    try {
      const result = await MMSService.getCategories(data);
      return result;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  },
  {
    condition: ({ dossier }, { getState }) => {
      const { categoriesState } = getState().mms;
      if (categoriesState.isLoading) {
        return false;
      }
      if (
        categoriesState.isSuccess &&
        categoriesState.dossier === dossier &&
        categoriesState.categories?.length > 0
      ) {
        return false;
      }
      return true;
    },
  }
);

export const addProductLink = createAsyncThunk(
  "mms/addProductLink",
  async (data, thunkAPI) => {
    try {
      let ToastLoading = toast.loading("Producten linken...", {
        closeOnClick: true,
      });

      const result = await MMSService.addProductLink(data);
      toast.update(ToastLoading, {
        render: `Ok!`,
        type: "success",
        isLoading: false,
        autoClose: 5000,
        theme: "colored",
        closeOnClick: true,
      });
      return result;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getProductgroepen = createAsyncThunk(
  "mms/getProductgroepen",
  async (data, thunkAPI) => {
    try {
      const result = await MMSService.getProductgroepen();
      return result;
    } catch (error) {
      const message = getMessageFromError(error);
      error.toString();
      errorHandling(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const MMSSlice = createSlice({
  name: "mms",
  initialState: {
    ...initialState,
    searchParams_temp: initialState.searchParams,
  },
  reducers: {
    reset: (state) => {
      return {
        ...initialState,
        searchParams_temp: initialState.searchParams,
        categories: state.categories,
      };
    },
    setSearchParams: (state, action) => {
      const {
        reset = false,
        forceSearch = false,
        keepProducts = false,
        ...searchParamsUpdate
      } = action.payload;

      if (!keepProducts) {
        state.products = [];
      }
      if (forceSearch && !keepProducts) {
        state.isLoading = true;
      }

      state.searchParams = {
        ...state.searchParams,
        ...(reset ? initialState.searchParams : {}),
        ...searchParamsUpdate,
      };
      if (reset) {
        state.searchParams_temp = {
          ...initialState.searchParams,
        };
      }

      state.forceSearch = forceSearch;
    },
    setSearchParams_temp: (state, action) => {
      const {
        reset = false,
        forceSearch = false,
        ...searchParamsUpdate
      } = action.payload;
      state.searchParams_temp = {
        ...state.searchParams_temp,
        ...(reset ? initialState.searchParams_temp : {}),
        ...searchParamsUpdate,
      };
      state.forceSearch = forceSearch;
      if (reset) {
        state.products_temp = [];
      }
    },
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    sortProducts(state, action) {
      const { sortBy, isAsc } = action.payload;
      state.sortBy = sortBy;
      state.isAsc = isAsc;
      state.products = sortProductsArray(state.products, sortBy, isAsc);
    },
    showMore(state) {
      state.products = state.products_temp;
      state.products_temp = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SearchProducts.pending, (state, action) => {
        if (searchDestination(action.meta?.arg?.to) === "products_temp") {
          state.isLoadingTemp = true;
        } else {
          state.isLoading = true;
        }
      })
      .addCase(SearchProducts.fulfilled, (state, action) => {
        if (action.payload.to === "products_temp") {
          state.products_temp = action.payload.products || [];
          state.isLoadingTemp = false;
        } else {
          state.products = sortProductsArray(
            action.payload.products || [],
            state.sortBy,
            state.isAsc
          );
          state.isLoading = false;
        }
        state.isSuccess = true;
        state.forceSearch = false;
      })
      .addCase(SearchProducts.rejected, (state, action) => {
        if (action.payload === STALE_SEARCH_REJECTED) {
          return;
        }
        if (searchDestination(action.meta?.arg?.to) === "products_temp") {
          state.isLoadingTemp = false;
        } else {
          state.isLoading = false;
        }
        state.forceSearch = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getRecommendedProducts.pending, (state) => {
        state.isLoadingRecommendedProducts = true;
      })
      .addCase(getRecommendedProducts.fulfilled, (state, action) => {
        state.isLoadingRecommendedProducts = false;
        state.isSuccess = true;
        state.recommendedProducts = action.payload.products || [];
      })
      .addCase(getRecommendedProducts.rejected, (state, action) => {
        state.isLoadingRecommendedProducts = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getCategories.pending, (state) => {
        state.categoriesState.isLoading = true;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.categoriesState.isLoading = false;
        state.categoriesState.isSuccess = true;
        state.categoriesState.categories = action.payload;
        state.categoriesState.dossier = action.meta.arg?.dossier ?? null;
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.categoriesState.isLoading = false;
        state.categoriesState.isError = true;
        state.categoriesState.categories = [];
      })
      .addCase(addProductLink.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addProductLink.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(addProductLink.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      });
  },
});

export const {
  reset,
  setSearchParams,
  setSearchParams_temp,
  setProducts,
  sortProducts,
  showMore,
} = MMSSlice.actions;
export default MMSSlice.reducer;
